import { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Row, Col, Card, Space, Tag, Switch } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, ReloadOutlined, DesktopOutlined, KeyOutlined } from '@ant-design/icons';
import { userAPI, screenAPI } from '../services/api';
import { themeColors } from '../config/theme';

const ROLE_CLIENT = 2;
const ROLE_SUB_ADMIN = 3;

const ROLE_OPTIONS = [
  { label: 'Client', value: ROLE_CLIENT },
  { label: 'Sub Admin', value: ROLE_SUB_ADMIN },
];

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [screensModal, setScreensModal] = useState({ open: false, userId: null });
  const [screensForm] = Form.useForm();
  const [screensLoading, setScreensLoading] = useState(false);
  const [passwordModal, setPasswordModal] = useState({ open: false, userId: null });
  const [passwordForm] = Form.useForm();
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => {
    fetchClients();
    fetchScreens();
  }, [pagination]);

  const fetchScreens = async () => {
    try {
      const response = await screenAPI.list({ page: 1, limit: 100 });
      setScreens(response.data.data.data);
    } catch (error) {
      console.error('Failed to load screens');
    }
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await userAPI.list({
        page: pagination.page,
        limit: pagination.limit,
      });
      setClients(response.data.data.data);
      setTotal(response.data.data.pagination.total);
    } catch (error) {
      message.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async (values) => {
    try {
      let userId = editingId;

      if (editingId) {
        await userAPI.update(editingId, {
          fullName: values.full_name,
          companyName: values.company_name,
          companyAddress: values.company_address,
          phone: values.phone,
          taxId: values.tax_id,
          isActive: values.is_active,
          roleId: values.roleId,
        });
        message.success('Client updated successfully');
      } else {
        const response = await userAPI.create({
          email: values.email,
          password: values.password,
          fullName: values.full_name,
          companyName: values.company_name,
          companyAddress: values.company_address,
          phone: values.phone,
          taxId: values.tax_id,
          roleId: values.roleId,
        });
        userId = response.data.data.id;
        message.success('User registered successfully');
      }

      await userAPI.assignScreens(userId, values.screenIds || []);

      setIsModalOpen(false);
      form.resetFields();
      setEditingId(null);
      fetchClients();
    } catch (error) {
      message.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = async (record) => {
    setEditingId(record.id);
    form.setFieldsValue({
      ...record,
      is_active: !!record.is_active,
      roleId: record.role_id,
    });
    setIsModalOpen(true);

    try {
      const response = await userAPI.getAssignedScreens(record.id);
      form.setFieldsValue({ screenIds: response.data.data.map((s) => s.id) });
    } catch (error) {
      message.error('Failed to load assigned screens');
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Delete Client',
      content: 'Are you sure you want to delete this client?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await userAPI.delete(id);
          message.success('Client deleted successfully');
          fetchClients();
        } catch (error) {
          message.error('Failed to delete client');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Company Name',
      dataIndex: 'company_name',
      key: 'company_name',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Tax ID',
      dataIndex: 'tax_id',
      key: 'tax_id',
    },
    {
      title: 'Role',
      dataIndex: 'role_id',
      key: 'role_id',
      render: (roleId) => (
        <Tag color={roleId === ROLE_SUB_ADMIN ? 'blue' : 'default'}>
          {roleId === ROLE_SUB_ADMIN ? 'SUB ADMIN' : 'CLIENT'}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'ACTIVE' : 'INACTIVE'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            icon={<DesktopOutlined />}
            title="Assign Screens"
            onClick={() => handleOpenAssignScreens(record.id)}
          />
          <Button
            size="small"
            icon={<KeyOutlined />}
            title="Change Password"
            onClick={() => setPasswordModal({ open: true, userId: record.id })}
          />
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  const handleOpenAssignScreens = async (userId) => {
    setScreensModal({ open: true, userId });
    setScreensLoading(true);
    try {
      const response = await userAPI.getAssignedScreens(userId);
      const assignedIds = response.data.data.map((s) => s.id);
      screensForm.setFieldsValue({ screenIds: assignedIds });
    } catch (error) {
      message.error('Failed to load assigned screens');
    } finally {
      setScreensLoading(false);
    }
  };

  const handleSaveAssignScreens = async (values) => {
    try {
      await userAPI.assignScreens(screensModal.userId, values.screenIds || []);
      message.success('Screens assigned successfully');
      setScreensModal({ open: false, userId: null });
      screensForm.resetFields();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to assign screens');
    }
  };

  const handleResetPassword = async (values) => {
    try {
      setPasswordSaving(true);
      await userAPI.resetPassword(passwordModal.userId, values.newPassword);
      message.success('Password reset successfully');
      setPasswordModal({ open: false, userId: null });
      passwordForm.resetFields();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingId(null);
              form.resetFields();
              setIsModalOpen(true);
            }}
          >
            Register Client
          </Button>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={fetchClients} loading={loading} />
        </Col>
      </Row>

      <Card style={{ backgroundColor: themeColors.bgElevated, borderColor: themeColors.border }}>
        <Table
          columns={columns}
          dataSource={clients}
          loading={loading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total,
            onChange: (page, pageSize) => {
              setPagination({ page, limit: pageSize });
            },
          }}
          rowKey="id"
          style={{ color: themeColors.text }}
        />
      </Card>

      <Modal
        title={editingId ? 'Edit Client' : 'Register Client'}
        open={isModalOpen}
        onOk={() => form.submit()}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
          setEditingId(null);
        }}
        style={{ backgroundColor: themeColors.bgElevated }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateOrUpdate}
          style={{ marginTop: '20px' }}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, message: 'Email is required' }, { type: 'email', message: 'Invalid email' }]}
          >
            <Input placeholder="client@example.com" disabled={!!editingId} />
          </Form.Item>

          {!editingId && (
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Password is required' }, { min: 6, message: 'Minimum 6 characters' }]}
            >
              <Input.Password placeholder="Password" />
            </Form.Item>
          )}

          <Form.Item name="roleId" label="Role" initialValue={ROLE_CLIENT}>
            <Select options={ROLE_OPTIONS} />
          </Form.Item>

          <Form.Item name="screenIds" label="Assigned Screens">
            <Select
              mode="multiple"
              placeholder="Select screens"
              options={screens.map((s) => ({ label: s.screen_name, value: s.id }))}
            />
          </Form.Item>

          <Form.Item name="full_name" label="Contact Name">
            <Input placeholder="e.g., John Doe" />
          </Form.Item>

          <Form.Item name="company_name" label="Company Name">
            <Input placeholder="e.g., Sanya Tour Co., Ltd." />
          </Form.Item>

          <Form.Item name="company_address" label="Company Address">
            <Input.TextArea placeholder="e.g., 123 Soi 14, Bangkok" rows={2} />
          </Form.Item>

          <Form.Item name="phone" label="Phone">
            <Input placeholder="e.g., +66 12 345 6789" />
          </Form.Item>

          <Form.Item name="tax_id" label="Tax ID">
            <Input placeholder="e.g., 1234567890123" />
          </Form.Item>

          {editingId && (
            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title="Assign Screens"
        open={screensModal.open}
        onOk={() => screensForm.submit()}
        onCancel={() => {
          setScreensModal({ open: false, userId: null });
          screensForm.resetFields();
        }}
        confirmLoading={screensLoading}
        style={{ backgroundColor: themeColors.bgElevated }}
      >
        <Form
          form={screensForm}
          layout="vertical"
          onFinish={handleSaveAssignScreens}
          style={{ marginTop: '20px' }}
        >
          <Form.Item name="screenIds" label="Assigned Screens">
            <Select
              mode="multiple"
              placeholder="Select screens"
              options={screens.map((s) => ({ label: s.screen_name, value: s.id }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Change Password"
        open={passwordModal.open}
        onOk={() => passwordForm.submit()}
        onCancel={() => {
          setPasswordModal({ open: false, userId: null });
          passwordForm.resetFields();
        }}
        confirmLoading={passwordSaving}
      >
        <Form form={passwordForm} layout="vertical" onFinish={handleResetPassword} style={{ marginTop: '20px' }}>
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[{ required: true, message: 'Password is required' }, { min: 6, message: 'Minimum 6 characters' }]}
          >
            <Input.Password placeholder="New password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
